'use client'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
  ComponentProps,
  ComponentType,
  PropsWithChildren,
  ReactNode,
  startTransition,
  useId,
  useRef,
} from 'react'
import { useFormStatus } from 'react-dom'
import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form'
import { Checkbox } from '../ui/checkbox'
import { MultiSelect } from '../ui/multi-select'

interface BaseField<T extends FieldValues> {
  name: FieldPath<T>
  label?: string | ReactNode
  labelRightContent?: ReactNode
  description?: string
  type: string
  placeholder?: string
  disabled?: boolean
  testId?: string
}

interface TextField<T extends FieldValues> extends BaseField<T> {
  type: 'text' | 'email' | 'password'
}

interface TextareaField<T extends FieldValues> extends BaseField<T> {
  type: 'textarea'
}

interface HiddenField<T extends FieldValues> extends BaseField<T> {
  type: 'hidden'
}

interface SelectField<T extends FieldValues> extends BaseField<T> {
  type: 'select' | 'multiselect'
  options: { label: string; value: string }[]
}

interface CheckboxField<T extends FieldValues> extends BaseField<T> {
  type: 'checkbox'
}

interface CustomField<T extends FieldValues> extends Omit<BaseField<T>, 'name'> {
  type: 'custom'
  component: ComponentType<any>
  name: string
}

interface PhoneField<T extends FieldValues> extends BaseField<T> {
  type: 'phone'
}

interface NumberField<T extends FieldValues> extends BaseField<T> {
  type: 'number'
}

type FieldUnion<T extends FieldValues> =
  | SelectField<T>
  | TextareaField<T>
  | TextField<T>
  | CheckboxField<T>
  | HiddenField<T>
  | CustomField<T>
  | PhoneField<T>
  | NumberField<T>

type FormRowField<T extends FieldValues> = FieldUnion<T> & {
  weight?: number
}

type FormFieldConfig<T extends FieldValues> =
  | FieldUnion<T>
  | { title?: string; fields: FormRowField<T>[] }

type BaseFormProps<T extends FieldValues> = {
  form: UseFormReturn<T>
  fields: FormFieldConfig<T>[]
  children?: ReactNode
  submitButton: {
    label: string
    leftContent?: ReactNode
    disabled?: boolean
    testId?: string
  }
}

type FormWithActionProps<T extends FieldValues> = BaseFormProps<T> & {
  action: (data: FormData) => void
}
type FormWithSubmitHandler<T extends FieldValues> = BaseFormProps<T> & {
  onSubmit: (data: T) => void
}

type DynamicFormProps<T extends FieldValues> = FormWithActionProps<T> | FormWithSubmitHandler<T>

function SubmitButton({
  children,
  disabled,
  testId,
}: PropsWithChildren<{ disabled?: boolean; testId?: string }>) {
  const status = useFormStatus()

  return (
    <Button
      disabled={status.pending || disabled}
      type="submit"
      className="cursor-pointer"
      data-testid={testId}
    >
      {status.pending ? 'Submitting...' : children}
    </Button>
  )
}

function payloadToFormData<T extends FieldValues>(payload: T) {
  const formData = new FormData()
  for (const key in payload) {
    formData.append(key, payload[key])
  }
  return formData
}

export function DynamicForm<T extends FieldValues>({
  form,
  fields,
  children,
  submitButton,
  ...rest
}: DynamicFormProps<T> & Omit<ComponentProps<'form'>, 'onSubmit' | 'action'>) {
  const id = useId()
  const ref = useRef<HTMLFormElement>(null)

  const renderField = (field: FieldUnion<T>) => {
    if (field.type === 'custom') {
      return <field.component key={id + String(field.name)} />
    }

    return (
      <FormField
        key={id + String(field.name)}
        control={form.control}
        name={field.name}
        render={({ field: formField }) => (
          <FormItem className={field.type === 'hidden' ? 'hidden' : ''}>
            {field.label &&
              field.type !== 'checkbox' &&
              (field.labelRightContent ? (
                <div className="flex items-end justify-between">
                  <FormLabel>{field.label}</FormLabel>
                  {field.labelRightContent}
                </div>
              ) : (
                <FormLabel>{field.label}</FormLabel>
              ))}

            {field.type === 'textarea' ? (
              <FormControl>
                <Textarea
                  placeholder={field.placeholder}
                  data-testid={field.testId}
                  {...formField}
                />
              </FormControl>
            ) : field.type === 'select' ? (
              <Select
                onValueChange={formField.onChange}
                defaultValue={formField.value}
                {...formField}
              >
                <FormControl>
                  <SelectTrigger data-testid={field.testId}>
                    <SelectValue placeholder={field.placeholder} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {field.options?.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : field.type === 'multiselect' ? (
              <MultiSelect
                options={field.options}
                onValueChange={formField.onChange}
                defaultValue={formField.value}
                data-testid={field.testId}
              />
            ) : field.type == 'checkbox' ? (
              <div className={'flex items-center gap-2.5'}>
                <FormControl>
                  <Checkbox
                    checked={formField.value}
                    onCheckedChange={formField.onChange}
                    data-testid={field.testId}
                  />
                </FormControl>
                <FormLabel>{field.label}</FormLabel>
              </div>
            ) : field.type === 'hidden' ? (
              <Input
                type="hidden"
                data-testid={field.testId}
                {...formField}
              />
            ) : (
              <FormControl>
                <Input
                  type={field.type}
                  placeholder={field.placeholder}
                  disabled={field.disabled}
                  data-testid={field.testId}
                  {...formField}
                />
              </FormControl>
            )}
            {field.description && <FormDescription>{field.description}</FormDescription>}
            <FormMessage />
          </FormItem>
        )}
      />
    )
  }

  return (
    <Form {...form}>
      <form
        ref={ref}
        {...(rest &&
          Object.fromEntries(
            Object.entries(rest).filter(([key]) => key !== 'action' && key !== 'onSubmit'),
          ))}
        {...('action' in rest
          ? {
              onSubmit: form.handleSubmit(() => {
                const formData = payloadToFormData(form.getValues())
                startTransition(() => {
                  rest.action(formData)
                })
              }),
            }
          : {})}
        {...('onSubmit' in rest
          ? {
              onSubmit: (e) => {
                e.preventDefault()
                form.handleSubmit(rest.onSubmit)
              },
            }
          : {})}
        className="space-y-4"
      >
        {fields.map((fieldConfig, index) => {
          if ('fields' in fieldConfig) {
            if (fieldConfig.title) {
              return (
                <div className="border-border space-y-4 border-y py-4">
                  <p className="text-lg font-semibold">{fieldConfig.title}</p>
                  {fieldConfig.fields.map((field) => (
                    <div
                      key={String(field.name)}
                      className="grow"
                      style={{
                        flex: field.weight,
                      }}
                    >
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              )
            }
            return (
              <div
                key={index}
                className="flex items-start gap-4"
              >
                {fieldConfig.fields.map((field) => (
                  <div
                    key={String(field.name)}
                    className="grow"
                    style={{
                      flex: field.weight,
                    }}
                  >
                    {renderField(field)}
                  </div>
                ))}
              </div>
            )
          }
          return renderField(fieldConfig)
        })}
        <div
          className={cn({
            'flex items-start': true,
            'justify-end': !submitButton.leftContent,
            'justify-between': submitButton.leftContent,
          })}
        >
          {submitButton.leftContent}
          <SubmitButton
            disabled={submitButton.disabled}
            testId={submitButton.testId}
          >
            {submitButton.label}
          </SubmitButton>
        </div>
        {children}
      </form>
    </Form>
  )
}
